import { useRefreshAlgoliaData } from "entities/algolia/hooks/useRefreshAlgoliaData";
import useRolePermission from "entities/auth/hooks/useRolePermission";
import { invalidateTagsListQuery } from "entities/tags/queries/useTagsListQuery";
import { useCreateTagTypeMutation } from "entities/themeTagTypes/queries/useCreateTagTypeMutation";
import { useThemePrimaryTagsQuery } from "entities/themeTagTypes/queries/useThemePrimaryTagsQuery";
import { CreateThemeTagType } from "entities/themeTagTypes/types";
import { hasThemeTagByName } from "entities/themeTags/api/hasThemeTagByName";
import { useCreateThemeTagMutation } from "entities/themeTags/queries/useCreateThemeTagMutation";
import {
  invalidateThemeTagDetailsQuery,
  useThemeTagDetailsQuery,
} from "entities/themeTags/queries/useThemeTagDetailsQuery";
import { useUpdateThemeTagMutation } from "entities/themeTags/queries/useUpdateThemeTagMutation";
import { CreateThemeTag, UpdateThemeTag } from "entities/themeTags/types";
import React, { ChangeEvent, FC, useEffect, useMemo, useState } from "react";
import useInputState, { InputHookResult } from "shared/hooks/useInputState";
import { Translations } from "shared/types/languages";
import { createContext } from "shared/utils/context";
import { useThemeTagsPage } from "../../../context";
import {
  FormattedTagType,
  TagDetailsProviderProps,
  TagDetailsTabs,
  TagDetailsType,
} from "./types";

const INITIAL_DELAY_MS = 2000;
const REFRESH_INTERVAL_MS = 5000;

const [Provider, useThemeTagDetails] = createContext<TagDetailsType>({
  name: "ThemeTagDetailsContext",
  hookName: "useThemeTagDetails",
  providerName: "ThemeTagDetailsProvider",
});

const ThemeTagDetailsProvider: FC<TagDetailsProviderProps> = ({ children }) => {
  const { allowed: hasReadAccess } = useRolePermission("themeTags:read");
  const { allowed: hasReadTranslationsAccess } = useRolePermission(
    "themeTags:translations:read",
  );
  const { themeTagsDetailsDrawer } = useThemeTagsPage();
  const { drawerDetails } = themeTagsDetailsDrawer;
  const [selectedTab, setSelectedTab] = useState(TagDetailsTabs.General);
  const [tagId, setTagId] = useState<string>();
  const tagName: InputHookResult = useInputState("tagName", "");
  const tagDescription: InputHookResult = useInputState("tagDescription", "");
  const [tagType, setTagType] = useState<FormattedTagType | null>(null);
  const tagTypeInput: InputHookResult = useInputState("tagType", "");
  const [errors, setErrors] = useState<string>("");
  const { tableData: tags } = useThemeTagsPage();
  const [translations, setTranslations] = useState<Translations | undefined>();
  const { refreshAlgoliaData } = useRefreshAlgoliaData();

  const { data: themePrimaryTags, isLoading: isLoadingTagTypes } =
    useThemePrimaryTagsQuery();
  const { data: tagData, isLoading } = useThemeTagDetailsQuery(
    drawerDetails?.id || "",
  );

  const { mutate: updateTagDetails, isPending: isSubmittingTag } =
    useUpdateThemeTagMutation();

  const { mutate: createTagDetails, isPending } = useCreateThemeTagMutation();

  const { mutate: createTagType, isPending: isSubmittingTagType } =
    useCreateTagTypeMutation();

  const setDefaultTag = () => {
    tagName.setValue("");
    tagDescription.setValue("");
    tagName?.setErrors([]);
    setTagType(null);
    tagTypeInput.setValue("");
    setTranslations(undefined);
  };

  const disabled = useMemo(
    () =>
      !tagName?.input.value ||
      Boolean(tagName.input.errors.length || !tagType?.id),
    [tagName.input.errors.length, tagName.input.value, tagType?.id],
  );

  useEffect(() => {
    if (tagData) {
      setTagId(tagData?.id);
      tagName.setValue(tagData.name);
      tagDescription.setValue(tagData.description);
      setTranslations(tagData.translations);

      const primaryTagData = themePrimaryTags?.find(
        (tagType) => tagData.tagType === tagType.id,
      );

      setTagType({
        id: primaryTagData?.id || "",
        name: primaryTagData?.name || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagData, isLoadingTagTypes]);

  useEffect(() => {
    if (hasReadAccess) {
      return;
    }

    if (hasReadTranslationsAccess) {
      setSelectedTab(TagDetailsTabs.Translations);
    }
  }, [hasReadAccess, hasReadTranslationsAccess, tagName]);

  const performTagNameError = (valueIsExist: boolean) => {
    if (valueIsExist) {
      return tagName?.setErrors([
        { key: "tagName", message: "This tag name is already exists" },
      ]);
    }

    return tagName?.setErrors([]);
  };

  const onChangeTagName = (e: ChangeEvent<HTMLInputElement>) => {
    tagName?.input.onChange(e);
    tagName?.setErrors([]);
  };

  const onCreateTagType = async (tagType: CreateThemeTagType) => {
    return createTagType({
      themeTagType: tagType,
      handleThemeTagType: setTagType,
    });
  };

  const onUpdateTagDetails = async (tagId: string, tag: UpdateThemeTag) => {
    return updateTagDetails({ themeTagId: tagId, themeTag: tag });
  };

  const onCreateTag = async (tag: CreateThemeTag) => {
    return createTagDetails({ themeTag: tag });
  };

  const onSubmitTag = async () => {
    if (disabled) {
      return;
    }

    try {
      const tagToSubmit = {
        name: tagName?.input.value.trim(),
        description: tagDescription?.input.value,
        matchingGamesCount: tagData ? tagData.matchingGamesCount : 0,
        tagType: tagType?.id || "",
        tagTypeName: tagType?.name || "",
        translations: translations || {},
        updatedAt: new Date(),
      };

      const isTagExist = await hasThemeTagByName(tagToSubmit.name, tagId);

      const isDuplicateTag = tags.data.some(
        (tag) =>
          tag.tagType === tagToSubmit.tagType && tag.name === tagToSubmit.name,
      );

      if (isTagExist && isDuplicateTag) {
        return performTagNameError(isTagExist);
      }

      if (tagId) {
        await onUpdateTagDetails(tagId, { ...tagToSubmit });
        invalidateThemeTagDetailsQuery(tagId);
      } else {
        await onCreateTag({ ...tagToSubmit });
      }

      // Invalidate query and refetch Algolia
      refreshAlgoliaData(INITIAL_DELAY_MS);
      refreshAlgoliaData(REFRESH_INTERVAL_MS);

      themeTagsDetailsDrawer.closeDrawer();
      setTagId(undefined);
      setDefaultTag();
      invalidateTagsListQuery();
    } catch (e: any) {
      console.error(e);
    }
  };

  const onSubmitTagTranslation = async () => {
    if (disabled || !tagId) {
      return;
    }

    try {
      const tagToSubmit = {
        translations: translations || {},
        updatedAt: new Date(),
      };

      await onUpdateTagDetails(tagId, { ...tagToSubmit });
      invalidateThemeTagDetailsQuery(tagId);

      // Invalidate query and refetch Algolia
      refreshAlgoliaData(INITIAL_DELAY_MS);
      refreshAlgoliaData(REFRESH_INTERVAL_MS);

      themeTagsDetailsDrawer.closeDrawer();
      setTagId(undefined);
      setDefaultTag();
      invalidateTagsListQuery();
    } catch (e: any) {
      console.error(e);
    }
  };

  const onSubmitTagType = async () => {
    if (!tagTypeInput.input.value.length) {
      return;
    }

    try {
      const tagTypeToSubmit = {
        name: tagTypeInput.input.value.trim(),
        createdAt: new Date().toISOString(),
      };

      await onCreateTagType(tagTypeToSubmit);
    } catch (e: any) {
      console.error(e);
    }
  };

  const onCloseTagDetails = () => {
    setDefaultTag();
    themeTagsDetailsDrawer.closeDrawer();
    setTagId(undefined);
    setSelectedTab(TagDetailsTabs.General);
  };

  const value = {
    loading: isLoading,
    submitting: isSubmittingTag || isPending || isSubmittingTagType,
    isTagTypesLoading: isSubmittingTagType || isLoadingTagTypes,
    selectedTab,
    setSelectedTab,
    translations,
    setTranslations,
    errors,
    tagId,
    tagName,
    tagDescription,
    disabled,
    onSubmitTag,
    onSubmitTagTranslation,
    onSubmitTagType,
    tagType,
    tagTypeInput,
    themePrimaryTags,
    tagTypes: themePrimaryTags,
    setTagType,
    setTagId,
    setDefaultTag,
    setErrors,
    onChangeTagName,
    onCloseTagDetails,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { ThemeTagDetailsProvider, useThemeTagDetails };
